import { queryAll } from '../lib/query';
import gtmDataLayerPush from '../lib/gtmDataLayerPush';

class EnumValues {
	/**
	 * @returns {Set<string>|Map<number,string>}
	 */
	static values(userMap = false) {
		if (!this._setCache) {
			const values = Object.entries(Object.getOwnPropertyDescriptors(this))
				.filter(([key, desc]) => typeof desc.get === 'function')
				.map(([key]) => (userMap ? [this[key], key.toLowerCase()] : this[key]));

			this._setCache = userMap ? new Map(values) : new Set(values);
		}
		return this._setCache;
	}

	/**
	 * @param {*} value
	 * @returns {boolean}
	 */
	static isValid(value) {
		return this.values().has(value);
	}
}

export class GtmFunnelStep extends EnumValues {
	static get WELCOME() {
		return 0;
	}

	static get LOGIN_REGISTER_METHOD_CHOOSE() {
		return 1;
	}

	static get ENTER_PERSONAL_DETAILS() {
		return 2;
	}

	static get REGISTER_OTP() {
		return 3;
	}

	static get TERM_POPUP() {
		return 4;
	}

	static get REGISTER_SUCCESS() {
		return 5;
	}

	/**
	 * @returns {Map<number,string>}
	 */
	static values(userMap = true) {
		return super.values(true);
	}

	/**
	 * @param {number|string} key
	 * @returns {{[p: string]: *|string}|{}}
	 */
	static get(key) {
		const _key = Number(key);

		if (!this.values().has(_key)) {
			return {};
		}

		return {
			[GtmEventParameters.STEP_NUMBER]: _key,
			[GtmEventParameters.STEP_NAME]: this.values().get(_key),
		};
	}
}

export class GtmFallbackVal {
	static get EMPTY() {
		return 'na';
	}
}

export class GtmThirdParty {
	static get FB() {
		return 'facebook';
	}
}

export class GtmEvens extends EnumValues {
	static get FUNNEL_START() {
		return 'funnel_start';
	}

	static get FUNNEL_STEP() {
		return 'funnel_step';
	}

	static get FUNNEL_FINISH() {
		return 'funnel_finish';
	}

	static get FUNNEL_ERROR() {
		return 'funnel_error';
	}

	static get FUNNEL_CLICK() {
		return 'funnel_click';
	}

	static get FUNNEL_LOGIN() {
		return 'funnel_login';
	}

	static get START_GAME() {
		return 'start_game';
	}

	static get DEPOSIT() {
		return 'deposit';
	}

	static get OTD() {
		return 'otd';
	}

	static get SPEND_VIRTUAL_CURRENCY() {
		return 'spend_virtual_currency';
	}

	static get EARN_VIRTUAL_CURRENCY() {
		return 'earn_virtual_currency';
	}

	static get GAME_ERROR() {
		return 'game_error';
	}

	static get PAYMENT_START() {
		return 'payment_start';
	}

	static get PAYMENT_STEP() {
		return 'payment_step';
	}

	static get PAYMENT_FINISH() {
		return 'payment_finish';
	}

	static get PAYMENT_ERROR() {
		return 'payment_error';
	}

	static get PAYMENT_CLICK() {
		return 'payment_click';
	}

	static get APP_DOWNLOAD_CLICK() {
		return 'app_download_click';
	}

	static get CTA_CLICK() {
		return 'cta_click';
	}
}

export class GtmEventParameters extends EnumValues {
	static get APP_TYPE() {
		return 'app_type';
	}

	static get CLICK_TEXT() {
		return 'click_text';
	}

	static get ENVIRONMENT() {
		return 'environment';
	}

	static get TS_MICROS() {
		return 'ts_micros';
	}

	static get CLIENT_TYPE() {
		return 'client_type';
	}

	static get COMPONENT() {
		return 'component';
	}

	static get CONTEXT() {
		return 'context';
	}

	static get CURRENCY() {
		return 'currency';
	}

	static get DEPOSIT_NUM() {
		return 'deposit_num';
	}

	static get ERROR_CODE() {
		return 'error_code';
	}

	static get ERROR_MESSAGE() {
		return 'error_message';
	}

	static get ERROR_TEXT() {
		return 'error_text';
	}

	static get ERROR_TYPE() {
		return 'error_type';
	}

	static get GAME_ID() {
		return 'game_id';
	}

	static get GAME_NAME() {
		return 'game_name';
	}

	static get IDENTIFICATION_TYPE() {
		return 'identification_type';
	}

	static get IS_SIGN_UP() {
		return 'is_sign_up';
	}

	static get ITEM_ID() {
		return 'item_id';
	}

	static get ITEM_NAME() {
		return 'item_name';
	}

	static get MFT_ID() {
		return 'mft_id';
	}

	static get MFT_NAME() {
		return 'mft_name';
	}

	static get PLAYER_ID() {
		return 'player_id';
	}

	static get PRE_REG_USER_ID() {
		return 'pre_reg_user_id';
	}

	static get PROCESS_NAME() {
		return 'process_name';
	}

	static get STEP_NAME() {
		return 'step_name';
	}

	static get STEP_NUMBER() {
		return 'step_number';
	}

	static get TITLE() {
		return 'title';
	}

	static get TRANSACTION_ID() {
		return 'transaction_id';
	}

	static get VALUE() {
		return 'value';
	}

	static get VERSION_NUMBER() {
		return 'version_number';
	}

	static get VIRTUAL_CURRENCY_NAME() {
		return 'virtual_currency_name';
	}

	static get THIRD_PARTY_SIGN_UP() {
		return 'third_party_sign_up';
	}

	static get THIRD_PARTY_LOGIN() {
		return 'third_party_login';
	}

	static get THIRD_PARTY_NAME() {
		return 'third_party_name';
	}

	static get LOGIN_SOURCE() {
		return 'login_source';
	}
}

/**
 * @param {Object} payload
 * @returns {Record<string, string|number>}
 */
export const prepareGtmPayload = (payload = {}) => {
	return {
		...payload,
		[GtmEventParameters.ENVIRONMENT]: isHofProd ? 'production' : 'stage',
		[GtmEventParameters.TS_MICROS]: Date.now(),
	};
};

export default function gtmInit() {
	const gtmElements = queryAll('[data-gtm-event]');

	const documentTitle = document.title?.trim().toLowerCase();

	gtmElements.forEach((element) => {
		const gtmEvent = element.getAttribute('data-gtm-event');

		if (!GtmEvens.isValid(gtmEvent)) {
			return;
		}

		/**
		 * @type {string}
		 */
		const elementText = (() => {
			const text = element.innerText || element.querySelector('img')?.alt;
			return text?.length > 0
				? text.trim().toLowerCase()
				: GtmFallbackVal.EMPTY;
		})();

		/**
		 * @type {Map<string, string>}
		 */
		const dataSet = new Map();

		GtmEventParameters.values().forEach((parameter) => {
			const value = element.getAttribute(`data-gtm-${parameter}`)?.trim();
			if (value?.length > 0) {
				dataSet.set(parameter, value.toLowerCase());
			}
		});

		/**
		 * @param {string} parameter
		 * @param {string|number|undefined} fallback
		 * @returns {string}
		 */
		const getEventDataValue = (parameter, fallback = GtmFallbackVal.EMPTY) =>
			dataSet.has(parameter) ? dataSet.get(parameter) : fallback;

		/**
		 * @type {Map<string, Record<string, string|number>>}
		 */
		const eventSet = new Map();

		eventSet.set(GtmEvens.CTA_CLICK, {
			[GtmEventParameters.PROCESS_NAME]: getEventDataValue(
				GtmEventParameters.PROCESS_NAME,
				'hof'
			),
			[GtmEventParameters.CLICK_TEXT]: getEventDataValue(
				GtmEventParameters.CLICK_TEXT,
				elementText
			),
			[GtmEventParameters.TITLE]: getEventDataValue(
				GtmEventParameters.TITLE,
				elementText
			),
			[GtmEventParameters.CONTEXT]: getEventDataValue(
				GtmEventParameters.CONTEXT,
				documentTitle
			),
			[GtmEventParameters.COMPONENT]: getEventDataValue(
				GtmEventParameters.COMPONENT
			),
		});

		eventSet.set(GtmEvens.APP_DOWNLOAD_CLICK, {
			[GtmEventParameters.PROCESS_NAME]: getEventDataValue(
				GtmEventParameters.PROCESS_NAME,
				'hof'
			),
			[GtmEventParameters.APP_TYPE]: getEventDataValue(
				GtmEventParameters.APP_TYPE
			),
			[GtmEventParameters.CLICK_TEXT]: getEventDataValue(
				GtmEventParameters.CLICK_TEXT,
				elementText
			),
		});

		eventSet.set(
			GtmEvens.FUNNEL_START,
			(() => {
				const stepNumber = getEventDataValue(GtmEventParameters.STEP_NUMBER, 0);

				const eventData = {
					[GtmEventParameters.CLICK_TEXT]: getEventDataValue(
						GtmEventParameters.CLICK_TEXT,
						elementText
					),
					[GtmEventParameters.PROCESS_NAME]: getEventDataValue(
						GtmEventParameters.PROCESS_NAME,
						'log-in'
					),
					[GtmEventParameters.IDENTIFICATION_TYPE]: getEventDataValue(
						GtmEventParameters.IDENTIFICATION_TYPE
					),
					...GtmFunnelStep.get(stepNumber),
				};

				const thirdParty = [
					GtmEventParameters.THIRD_PARTY_NAME,
					GtmEventParameters.THIRD_PARTY_SIGN_UP,
					GtmEventParameters.THIRD_PARTY_LOGIN,
				];

				thirdParty.forEach((param) => {
					eventData[param] = getEventDataValue(param, GtmFallbackVal.EMPTY);
				});

				return eventData;
			})()
		);

		if (!eventSet.has(gtmEvent)) {
			return;
		}

		element.addEventListener('click', (e) => {
			gtmDataLayerPush(gtmEvent, prepareGtmPayload(eventSet.get(gtmEvent)));
		});
	});
}
